import { assertNever } from "@redotech/util/type";

/** Which checkout the walkthrough explains. A cart with a recurring plan gets
 * Shopify's subscription checkout, which has no HSA/FSA method. */
export enum HsaPurchaseType {
  SUBSCRIPTION = "subscription",
  ONE_TIME = "one_time",
}

/** One step of the "how paying with an HSA/FSA works" walkthrough. */
export interface HsaLearnMoreStep {
  title: string;
  image: string;
}

/** What the storefront knows about recurring selling plans. */
export interface HsaSubscriptionSignals {
  /** The PDP's selected variant can be bought on a recurring plan. Always
   * false off the PDP. */
  variantOffersSubscription: boolean;
  /** The cart already holds a recurring line. */
  cartHasSubscription: boolean;
}

export interface HsaLearnMoreTabs {
  /** Show the Subscription / One-time toggle; otherwise only one-time renders. */
  tabbed: boolean;
  initial: HsaPurchaseType;
}

/** Any subscription signal earns the toggle, but only a subscription already
 * in the cart opens on it. */
export function hsaLearnMoreTabs({
  variantOffersSubscription,
  cartHasSubscription,
}: HsaSubscriptionSignals): HsaLearnMoreTabs {
  return {
    tabbed: variantOffersSubscription || cartHasSubscription,
    initial: cartHasSubscription
      ? HsaPurchaseType.SUBSCRIPTION
      : HsaPurchaseType.ONE_TIME,
  };
}

const SUBSCRIPTION_STEPS: readonly HsaLearnMoreStep[] = [
  { title: "Pay with your credit card", image: "subscription-credit-card.png" },
  {
    title: "Reimburse on the thank-you page",
    image: "subscription-thank-you.png",
  },
  { title: "We cover each renewal", image: "subscription-claim-submitted.png" },
];

/** Mirrored in the Liquid theme block (`hsa-pdp-block.liquid`); step numbers
 * are positional, so the LMN-only step renumbers what follows it. */
export function hsaLearnMoreSteps(
  purchaseType: HsaPurchaseType,
  requiresLmn: boolean,
): readonly HsaLearnMoreStep[] {
  switch (purchaseType) {
    case HsaPurchaseType.ONE_TIME:
      return [
        {
          title: "Pick HSA/FSA at checkout",
          image: "pay-with-hsa-checkout.png",
        },
        ...(requiresLmn
          ? [
              {
                title: "Check your eligibility",
                image: "pay-with-hsa-eligibility.png",
              },
            ]
          : []),
        { title: "Pay with your HSA/FSA card", image: "pay-with-hsa-card.png" },
      ];
    case HsaPurchaseType.SUBSCRIPTION:
      // Same for LMN items by product decision: their reimbursement is planned.
      return SUBSCRIPTION_STEPS;
    default:
      return assertNever(purchaseType);
  }
}
